import type { ArticleVO } from '@core/types/article';
import type { RewardVo } from '@core/types/auto/heartbeat';
import type { OiSchoolVO, SingleCaseVo } from '@core/types/auto/oj';
import type {
  ApiRes,
  CityDataVO,
  ClassificationTagVO,
  IdNameOption,
  RealGradeVO,
  SearchProblemItemVO,
  SelectOption,
  UploadJudgeFileVO,
  UploadZipResVO,
} from '@core/types/common';
import { GATEWAY_PREFIX_COMMON } from '@core/utils/runtime-config';
import { httpService } from './http';

export function getProblemTag() {
  return httpService.get<IdNameOption[]>(
    `/api/code-community/api/get-problem-tag`,
  );
}
export function getProblemTags() {
  return httpService.get<IdNameOption[]>(
    `/api/code-community/api/get-problem-tags`,
  );
}
export function getClassificationList() {
  return httpService.get<ClassificationTagVO[]>(
    `/api/code-community/api/tag/get-classification-list`,
  );
}
export function getProblemDifficulty() {
  return httpService.get<IdNameOption[]>(
    `/api/code-community/api/get-problem-difficulty`,
  );
}

export function getProblemSource() {
  return httpService.get<IdNameOption[]>(
    `/api/code-community/api/get-problem-source`,
  );
}

export function getProblemUserStatus() {
  return httpService.get<IdNameOption[]>(
    `/api/code-community/api/get-problem-user-status`,
  );
}

export function getZoneDataList(types: string | string[]) {
  return httpService.get<Record<string, SelectOption[]>>(
    `/api/code-community/api/zone-data-list`,
    { query: { types } },
  );
}

export function getLanguage() {
  return httpService.get<IdNameOption[]>(
    `/api/code-community/api/get-language`,
  );
}
export function getSensitiveCheckResult(type = 'nickName', content = '') {
  return httpService.get<boolean>(
    `/api/code-community/api/get-sensitive-check-result`,
    { query: { type, content } },
  );
}

export function checkNickNameSensitive(content = '') {
  return getSensitiveCheckResult('nickName', content);
}

export function uploadZip(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return httpService.post<UploadZipResVO>(
    `/api/code-community/api/file/upload-zip`,
    {
      body: formData,
    },
  );
}

export function uploadJudgeFile(files: File[]) {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('file', files[i]);
  }
  return httpService.post<UploadJudgeFileVO[]>(
    `/api/code-community/gwpub/file/upload-files`,
    {
      body: formData,
    },
  );
}

export function uploadTestSampleFiles(files: File[], pid: number) {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('file', files[i]);
  }
  return httpService.post<SingleCaseVo>(
    `/api/code-community/gwpub/file/upload-test-case`,
    {
      body: formData,
      query: { pid },
    },
  );
}

export function uploadGroupJudgeFile(files: File[], gid: any) {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('file', files[i]);
  }
  return httpService.post<UploadJudgeFileVO[]>(
    `/api/code-community/gwpub/file/upload-group-files`,
    {
      body: formData,
      query: { gid },
    },
  );
}

// 上传单个case点文件：包括2个文件
// xxx.in文件和xxx.out文件
export function uploadJudgeCaseFile(files: File[], gid: number) {
  const formData = new FormData();
  for (let i = 0; i < files.length; i++) {
    formData.append('file', files[i]);
  }
  return httpService.post<SingleCaseVo>(
    `/api/code-community/gwpub/file/upload-group-case`,
    {
      body: formData,
      query: { gid },
    },
  );
}

// 下载单个case点文件：包括2个文件
// xxx.in文件和xxx.out文件
export function downloadJudgeCaseFile(query: { file: string[]; gid: number }) {
  return httpService.post<{ url: string }>(
    `/api/code-community/gwpub/file/download-group-case`,
    {
      query,
    },
  );
}

export function uploadAvatar(file: File) {
  const formData = new FormData();
  formData.append('image', file);
  return httpService.post<UploadJudgeFileVO[]>(
    `/api/code-community-forum/api/content/upload-avatar`,
    {
      body: formData,
    },
  );
}

export function uploadMarkdownFile(file: File) {
  const formData = new FormData();
  formData.append(`image`, file);
  return httpService.post<{ url: string }>(
    `/api/code-community-forum/api/upload/upload-image`,
    {
      body: formData,
    },
  );
}

export function uploadConsumerMarkdownFile(file: File) {
  const formData = new FormData();
  formData.append(`image`, file);
  return httpService.post<{ url: string }>(
    `/api/code-community-forum/api/content/upload-md-img`,
    {
      body: formData,
    },
  );
}

export function getArticleDetail(aid: number) {
  return httpService.get<ArticleVO>(
    `/api/code-community/api/get-announcement-info`,
    {
      query: { aid },
    },
  );
}

export function toggleArticleLike(aid: number, toLike: boolean) {
  return httpService.get(`/api/code-community/api/announcement-like`, {
    query: { aid, toLike },
  });
}

export function getRegionList() {
  return httpService.get<CityDataVO[]>(
    `${GATEWAY_PREFIX_COMMON}/api/get-region-list`,
  );
}

export function getSchoolList(keyword: string) {
  return httpService.get<IdNameOption[]>(
    `${GATEWAY_PREFIX_COMMON}/api/get-school-list`,
    { query: { keyword } },
  );
}

export function searchOISchool(schoolName: string) {
  return httpService.get<OiSchoolVO[]>(
    `/api/code-community/api/oi-school/search`,
    { query: { schoolName } },
  );
}

export function getGradeList() {
  return httpService.get<RealGradeVO[]>(
    `/api/code-community/api/get-grade-list`,
  );
}

export function getGrowthGradeList() {
  return httpService.get<RealGradeVO[]>(
    `/api/code-community-growth/api/common/get-grade-list`,
  );
}

export function searchProblem(keyword: string) {
  return httpService.get<SearchProblemItemVO[]>(
    `/api/code-community-forum/api/common/searchProblem`,
    { query: { keyword } },
  );
}
export function getAuthReward() {
  return httpService.get<RewardVo[]>(
    `/api/code-community-heart-beat/api/reward/v2/authReward`,
  );
}

export function getOiProvinces() {
  return httpService.get<string[]>(
    `/api/code-community/api/oi-school/provinces`,
  );
}
export function uploadAwardImage(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return httpService.post<{ url: string }>(
    `/api/code-community-forum/api/upload/upload-award-file`,
    {
      body: formData,
    },
  );
}

/**
 * 获取公开上传 token（用于农场分享等场景）,这是正式的接口，后端确定就用这个名字
 * @param dirType 目录类型
 */
export function getPublicUploadToken(dirType = 'slider') {
  return httpService.get(`/api/code-community/gwpub/file/token-for-test`, {
    query: { dirType },
  });
}
